import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import {
  QueryClient,
  QueryClientProvider,
  QueryClientConfig
} from '@tanstack/react-query'
import { AuthProvider, useAuth } from '../utils/auth'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { NotificationProvider } from './Elements/NotificationSystem'
import Layout from './Layout'
import { ApiContext } from './AppContext'

import LandingPage from './LandingPage'
import ProjectsRoutes from './Routers/ProjectsRouter'
import DashboardRoutes from './Routers/DashboardRouter'
import UsersRoutes from './Routers/UsersRouter'
import LeaderboardRoutes from './Routers/LeaderboardRouter'
import EpicsRoutes from './Routers/EpicsRouter'
import PerformanceDashboard from './Pages/PerformanceDashboard'

// Unified API base URL
const baseUrl = process.env.NODE_ENV === 'production'
  ? ''
  : 'http://localhost:4280'
const apiUrl = `${baseUrl}/api`

// Configure React Query with a default stale time of 10 minutes (600,000 ms)
const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 600_000
    }
  }
}
const queryClient = new QueryClient(queryClientConfig)

/**
 * Internal component that decides what to render based on authentication state.
 */
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-indigo-100">
    <h1 className="text-5xl font-extrabold tracking-tight text-indigo-700 mb-8 select-none">
      <span className="text-indigo-400">Jira</span>
      <span className="text-indigo-800">X</span>
    </h1>
    <div
      className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"
      role="status"
      aria-label="Loading"
    />
  </div>
)

const AppRoutes: React.FC = () => {
  const { user, isLoading, login } = useAuth()


  if (isLoading) {
    return <LoadingScreen />
  }

  // Unauthenticated – show landing page
  if (!user) {
    // Always show the landing page when unauthenticated; the SWA CLI
    // (in local development) or Static Web Apps service (in cloud)
    // will handle the actual authentication flow.
    return <LandingPage onLogin={login} />
  }

  // Authenticated and domain allowed – render the full application
  return (
    <ApiContext.Provider value={apiUrl}>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <Routes>
            <Route path='/' element={<Layout />}>
              <Route index element={<Navigate to='/dashboard' />} />
              <Route path='dashboard/*' element={<DashboardRoutes />} />
              <Route path='projects/*' element={<ProjectsRoutes />} />
              <Route path='users/*' element={<UsersRoutes />} />
              <Route path='leaderboard/*' element={<LeaderboardRoutes />} />
              <Route path='epics/*' element={<EpicsRoutes />} />
              <Route path='performance' element={<PerformanceDashboard />} />
              <Route path='*' element={<Navigate to='/dashboard' />} />
            </Route>
          </Routes>
          <ReactQueryDevtools initialIsOpen />
        </NotificationProvider>
      </QueryClientProvider>
    </ApiContext.Provider>
  )
}

const App: React.FC = () => (
  <AuthProvider>
    <AppRoutes />
  </AuthProvider>
)

export default App
